import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../../config";
import RecentAnnouncementsWidget from "../../announcements/RecentAnnouncementsWidget";
import s from "./CashierDashboard.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function studentDisplayName(st) {
  if (!st) return "Unknown student";
  const lf = [st.lastName, st.firstName].filter(Boolean).join(", ");
  return lf || st.studentNumber || "Unnamed student";
}
function peso(n) {
  const v = Number(n || 0);
  return "₱" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/**
 * Cashier / Finance dashboard. Surfaces the signals a cashier scans every
 * morning — money in today, this month, outstanding receivables, unpaid +
 * overdue invoices, and the live payment feed with OR# on every row.
 *
 * Read-only: no mutations, no permission elevation. Every fetch falls back
 * to [] on failure so a single backend hiccup never blocks the dashboard.
 */
export default function CashierDashboard({ user }) {
  const nav = useNavigate();

  const [schoolYears, setSchoolYears] = useState([]);
  const [semesters,   setSemesters]   = useState([]);
  const [payments,    setPayments]    = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [students,    setStudents]    = useState([]);
  const [sections,    setSections]    = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(true);

  const [syId,  setSyId]  = useState("");
  const [semId, setSemId] = useState("");

  // Detail modal target. We store the payment id (not the object) so the
  // modal always re-resolves against fresh state and never pins a stale
  // record if the payment list refetches mid-session.
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/tuition-payments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/tuition-invoices`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
    ]).then(([sy, sem, pay, inv, stu, sec, gl, emp]) => {
      if (cancelled) return;
      setSchoolYears(Array.isArray(sy)  ? sy  : []);
      setSemesters(Array.isArray(sem)   ? sem : []);
      setPayments(Array.isArray(pay)    ? pay : []);
      setInvoices(Array.isArray(inv)    ? inv : []);
      setStudents(Array.isArray(stu)    ? stu : []);
      setSections(Array.isArray(sec)    ? sec : []);
      setGradeLevels(Array.isArray(gl)  ? gl  : []);
      setEmployees(Array.isArray(emp)   ? emp : []);
      const activeSy  = (sy || []).find(y => y.active) || (sy || [])[0];
      if (activeSy) setSyId(String(activeSy.id));
      const activeSem = (sem || []).find(x => x.active)
        || (sem || []).find(x => !activeSy || String(x.schoolYearId) === String(activeSy.id))
        || (sem || [])[0];
      if (activeSem) setSemId(String(activeSem.id));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Reset semester if it stops belonging to the chosen SY.
  useEffect(() => {
    if (!syId) return;
    const stillValid = semesters.some(x => String(x.id) === semId && String(x.schoolYearId) === String(syId));
    if (stillValid) return;
    const inSy = semesters.filter(x => String(x.schoolYearId) === String(syId));
    const fresh = inSy.find(x => x.active) || inSy[0];
    setSemId(fresh ? String(fresh.id) : "");
  }, [syId, semId, semesters]);

  // ESC closes the payment detail modal. Listener mounted only while open.
  useEffect(() => {
    if (selectedPaymentId == null) return;
    const onKey = (e) => { if (e.key === "Escape") setSelectedPaymentId(null); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [selectedPaymentId]);

  // Fast lookups.
  const studentById  = useMemo(() => Object.fromEntries(students.map(x => [x.id, x])), [students]);
  const sectionById  = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const gradeById    = useMemo(() => Object.fromEntries(gradeLevels.map(x => [x.id, x])), [gradeLevels]);
  const invoiceById  = useMemo(() => Object.fromEntries(invoices.map(x => [x.id, x])), [invoices]);
  const employeeById = useMemo(() => Object.fromEntries(employees.map(x => [x.id, x])), [employees]);

  const activeSy  = useMemo(() => schoolYears.find(y => String(y.id) === syId) || null, [schoolYears, syId]);
  const activeSem = useMemo(() => semesters.find(x => String(x.id) === semId) || null, [semesters, semId]);

  // Time-window anchors used in nearly every KPI below.
  const now = Date.now();
  const todayStart = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime();
  }, []);
  const monthStart = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime();
  }, []);

  // Invoices scoped to selected SY + Semester. Every finance KPI funnels
  // through this set so a date / SY change consistently reshapes all the
  // numbers below.
  const scopedInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (syId  && String(inv.schoolYearId) !== String(syId))  return false;
      if (semId && String(inv.semesterId)   !== String(semId)) return false;
      return true;
    });
  }, [invoices, syId, semId]);

  const scopedInvoiceIds = useMemo(
    () => new Set(scopedInvoices.map(inv => inv.id)),
    [scopedInvoices]
  );

  // Payments belong to invoices, not directly to SY/Sem — filter via the
  // scoped invoice id set.
  const scopedPayments = useMemo(
    () => payments.filter(p => scopedInvoiceIds.has(p.invoiceId)),
    [payments, scopedInvoiceIds]
  );

  // ── KPIs ──────────────────────────────────────────────────────────────
  const todayCollected = useMemo(() => {
    let n = 0;
    for (const p of scopedPayments) {
      const t = p.paidAt ? new Date(p.paidAt).getTime() : 0;
      if (t >= todayStart) n += Number(p.amount || 0);
    }
    return n;
  }, [scopedPayments, todayStart]);

  const monthCollected = useMemo(() => {
    let n = 0;
    for (const p of scopedPayments) {
      const t = p.paidAt ? new Date(p.paidAt).getTime() : 0;
      if (t >= monthStart) n += Number(p.amount || 0);
    }
    return n;
  }, [scopedPayments, monthStart]);

  const todayTxCount = useMemo(() => {
    let n = 0;
    for (const p of scopedPayments) {
      const t = p.paidAt ? new Date(p.paidAt).getTime() : 0;
      if (t >= todayStart) n += 1;
    }
    return n;
  }, [scopedPayments, todayStart]);

  // Paid-per-invoice tally so we can compute the per-invoice remainder.
  const paidByInvoice = useMemo(() => {
    const m = new Map();
    for (const p of scopedPayments) {
      m.set(p.invoiceId, (m.get(p.invoiceId) || 0) + Number(p.amount || 0));
    }
    return m;
  }, [scopedPayments]);

  // Outstanding receivables across scoped invoices — sum of positive
  // remainders. Overpaid invoices don't subtract from the total.
  const outstanding = useMemo(() => {
    let n = 0;
    for (const inv of scopedInvoices) {
      const remaining = Number(inv.amount || 0) - (paidByInvoice.get(inv.id) || 0);
      if (remaining > 0.005) n += remaining;
    }
    return n;
  }, [scopedInvoices, paidByInvoice]);

  // Unpaid + partial invoice counts, and the subset that is overdue.
  const unpaidStats = useMemo(() => {
    let unpaid = 0, overdue = 0;
    for (const inv of scopedInvoices) {
      const st = (inv.status || "").toUpperCase();
      if (st !== "UNPAID" && st !== "PARTIAL") continue;
      unpaid += 1;
      const dueMs = inv.dueDate ? new Date(inv.dueDate).getTime() : null;
      if (dueMs != null && dueMs < now) overdue += 1;
    }
    return { unpaid, overdue };
  }, [scopedInvoices, now]);

  // ── Feeds ─────────────────────────────────────────────────────────────
  // Today's payments — newest first, ten visible in the panel.
  const todaysPayments = useMemo(() => {
    return [...scopedPayments]
      .filter(p => {
        const t = p.paidAt ? new Date(p.paidAt).getTime() : 0;
        return t >= todayStart;
      })
      .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime());
  }, [scopedPayments, todayStart]);

  // Overdue invoices — oldest due date first so the most stale rises to
  // the top of the panel and the cashier knows what to chase.
  const overdueInvoices = useMemo(() => {
    const list = [];
    for (const inv of scopedInvoices) {
      const st = (inv.status || "").toUpperCase();
      if (st !== "UNPAID" && st !== "PARTIAL") continue;
      const dueMs = inv.dueDate ? new Date(inv.dueDate).getTime() : null;
      if (dueMs == null || dueMs >= now) continue;
      const remaining = Number(inv.amount || 0) - (paidByInvoice.get(inv.id) || 0);
      if (remaining <= 0.005) continue;
      list.push({ inv, dueMs, remaining });
    }
    list.sort((a, b) => a.dueMs - b.dueMs);
    return list;
  }, [scopedInvoices, paidByInvoice, now]);

  // Recent payments — last 10 scoped events, newest first. Useful when
  // "today" is quiet (e.g. early morning) so the cashier still sees the
  // latest collection activity.
  const recentPayments = useMemo(() => {
    return [...scopedPayments]
      .filter(p => p.paidAt)
      .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())
      .slice(0, 10);
  }, [scopedPayments]);

  // Resolve a payment's full display context. Single source of truth so
  // every row + the detail modal show the same student / section / grade.
  function paymentContext(p) {
    const invoice   = invoiceById[p.invoiceId] || null;
    const student   = invoice ? studentById[invoice.studentId] : null;
    const section   = student?.currentSectionId ? sectionById[student.currentSectionId] : null;
    const grade     = section?.gradeLevelId ? gradeById[section.gradeLevelId] : null;
    const processor = p.receivedByStaffId ? employeeById[p.receivedByStaffId] : null;
    return { invoice, student, section, grade, processor };
  }

  const displayName = user?.linkedEntity?.displayName || user?.username || "";
  const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className={s.wrap}>
      {/* Hero */}
      <header className={s.header}>
        <div className={s.eyebrow}>CASHIER · DASHBOARD</div>
        <h1 className={s.title}>
          Welcome{displayName ? `, ${displayName}` : ""}.
        </h1>
        <p className={s.sub}>
          Daily finance view — collections today and this month, outstanding receivables,
          overdue invoices, and the live payment feed with OR&nbsp;# on every row.
        </p>
        <div className={s.heroMeta}>
          <span className={s.heroMetaPill}>Role: {user?.role || "Cashier"}</span>
          {user?.linkedEntity?.displayName && (
            <span className={s.heroMetaPill}>Linked: {user.linkedEntity.displayName}</span>
          )}
          <span className={s.heroMetaSpacer} />
          <span className={s.heroMetaSoft}>{monthLabel}</span>
        </div>
      </header>

      {/* Filter */}
      <div className={s.filterCard}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            {schoolYears.map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semId} onChange={e => setSemId(e.target.value)}>
            {semesters
              .filter(x => !syId || String(x.schoolYearId) === String(syId))
              .map(x => (
                <option key={x.id} value={x.id}>
                  {x.label}{x.active ? " · Active" : ""}
                </option>
              ))}
          </select>
        </label>
        <div className={s.filterActive}>
          {activeSy  && <span className={s.activePill}>{activeSy.label}</span>}
          {activeSem && <span className={s.activePill}>{activeSem.label}</span>}
        </div>
      </div>

      {/* Stat tiles */}
      <div className={s.statGrid}>
        <StatTile
          index={0} label="Collected Today"
          value={loading ? "…" : peso(todayCollected)}
          sub={loading ? "" : `${todayTxCount} payment${todayTxCount === 1 ? "" : "s"} today`}
          tone="ok" />
        <StatTile
          index={1} label="This Month"
          value={loading ? "…" : peso(monthCollected)}
          sub={loading ? "" : monthLabel}
          tone="ok" />
        <StatTile
          index={2} label="Outstanding"
          value={loading ? "…" : peso(outstanding)}
          sub={loading ? "" : "Across unpaid + partial invoices"}
          tone={outstanding > 0.005 ? "warn" : "ok"} />
        <StatTile
          index={3} label="Unpaid Invoices"
          value={loading ? "…" : unpaidStats.unpaid}
          sub={loading ? "" : "UNPAID + PARTIAL"}
          tone={unpaidStats.unpaid > 0 ? "warn" : "ok"} />
        <StatTile
          index={4} label="Overdue"
          value={loading ? "…" : unpaidStats.overdue}
          sub={loading ? "" : "Past due date · unpaid"}
          tone={unpaidStats.overdue > 0 ? "danger" : "ok"} />
        <StatTile
          index={5} label="Today's Tx"
          value={loading ? "…" : todayTxCount}
          sub={loading ? "" : "Payments recorded today"} />
      </div>

      {/* Today's payments + Overdue invoices */}
      <div className={s.panelGrid}>
        <section className={`${s.panel} ${s.panelWide}`}>
          <header className={s.panelHead}>
            <div>
              <h2 className={s.panelTitle}>Today&apos;s Payments</h2>
              <p className={s.panelSub}>
                Live feed of collections recorded today. Click any row for full receipt details.
              </p>
            </div>
            <a className={s.panelAction} href="/admin/tuition">Open Tuition →</a>
          </header>

          {loading ? (
            <div className={s.empty}>Loading…</div>
          ) : todaysPayments.length === 0 ? (
            <div className={s.empty}>
              No payments recorded today — the till is quiet. Yesterday&apos;s and earlier
              activity is in the Recent Payments feed below.
            </div>
          ) : (
            <ul className={s.feedList}>
              {todaysPayments.slice(0, 10).map((p, i) => {
                const ctx = paymentContext(p);
                const stName = studentDisplayName(ctx.student);
                const amt = Number(p.amount || 0);
                return (
                  <li
                    key={p.id}
                    style={{ "--i": Math.min(i, 12) }}
                    className={s.feedRow}>
                    <button
                      type="button"
                      className={s.feedRowBtn}
                      onClick={() => setSelectedPaymentId(p.id)}
                      aria-label={`Open payment details for ${stName}, ${peso(amt)}`}>
                      <div
                        className={s.feedAvatar}
                        style={{ background: avatarColor(stName) }}
                        aria-hidden="true">
                        {initials(stName)}
                      </div>
                      <div className={s.feedBody}>
                        <div className={s.feedTitle}>
                          <span className={s.feedName}>{stName}</span>
                          <span className={`${s.feedAmount} ${amt < 0 ? s.feedAmountNeg : ""}`}>
                            {amt < 0 ? "−" : ""}{peso(Math.abs(amt))}
                          </span>
                        </div>
                        <div className={s.feedMeta}>
                          {p.orNumber && (
                            <span className={s.orPill}>OR {p.orNumber}</span>
                          )}
                          <span>{p.method || "—"}</span>
                          {p.bankName && <span className={s.feedMetaSep}>·</span>}
                          {p.bankName && <span>{p.bankName}</span>}
                          <span className={s.feedMetaSep}>·</span>
                          <span>{formatDateTime(p.paidAt)}</span>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
              {todaysPayments.length > 10 && (
                <li className={s.feedMore}>
                  +{todaysPayments.length - 10} more today · open the Tuition tab to see the full ledger
                </li>
              )}
            </ul>
          )}
        </section>

        <section className={s.panel}>
          <header className={s.panelHead}>
            <div>
              <h2 className={s.panelTitle}>Overdue Invoices</h2>
              <p className={s.panelSub}>
                Oldest first — chase the most stale balances before they age further.
              </p>
            </div>
            <a className={s.panelAction} href="/admin/tuition">Tuition →</a>
          </header>

          {loading ? (
            <div className={s.empty}>Loading…</div>
          ) : overdueInvoices.length === 0 ? (
            <div className={s.empty}>
              No overdue invoices in the current scope. Everything is on time —
              well done.
            </div>
          ) : (
            <ul className={s.healthList}>
              {overdueInvoices.slice(0, 10).map(({ inv, dueMs, remaining }, i) => {
                const student = studentById[inv.studentId];
                const section = student?.currentSectionId ? sectionById[student.currentSectionId] : null;
                const grade   = section ? gradeById[section.gradeLevelId] : null;
                const stName  = studentDisplayName(student);
                const daysOverdue = Math.max(1, Math.floor((now - dueMs) / (24 * 60 * 60 * 1000)));
                return (
                  <li
                    key={inv.id}
                    style={{ "--i": i }}
                    className={`${s.healthRow} ${daysOverdue >= 14 ? s.healthRow_danger : s.healthRow_warn}`}>
                    <div className={s.healthAvatar} style={{ background: avatarColor(stName) }}>
                      {initials(stName)}
                    </div>
                    <div className={s.healthBody}>
                      <div className={s.healthName}>
                        {stName}
                        {grade && <span className={s.healthGrade}>· {grade.name}</span>}
                      </div>
                      <div className={s.healthMeta}>
                        <span className={s.badgeOutstanding}>{peso(remaining)} due</span>
                        <span className={`${s.badgeWarn} ${daysOverdue >= 14 ? s.badgeDanger : ""}`}>
                          {daysOverdue} day{daysOverdue === 1 ? "" : "s"} overdue
                        </span>
                        {inv.status && <span className={s.healthStatus}>{inv.status}</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
              {overdueInvoices.length > 10 && (
                <li className={s.healthMore}>
                  +{overdueInvoices.length - 10} more overdue · open Tuition to triage
                </li>
              )}
            </ul>
          )}
        </section>
      </div>

      {/* Recent payments — broader window than Today, helpful when today is quiet. */}
      <section className={s.activitySection}>
        <header className={s.panelHead}>
          <div>
            <h2 className={s.panelTitle}>Recent Payments</h2>
            <p className={s.panelSub}>
              Last 10 collections
              {activeSy ? ` for ${activeSy.label}` : ""}
              {activeSem ? ` · ${activeSem.label}` : ""}.
            </p>
          </div>
        </header>
        {loading ? (
          <div className={s.empty}>Loading…</div>
        ) : recentPayments.length === 0 ? (
          <div className={s.empty}>No recent payments for this scope.</div>
        ) : (
          <ul className={s.feedList}>
            {recentPayments.map((p, i) => {
              const ctx = paymentContext(p);
              const stName = studentDisplayName(ctx.student);
              const amt = Number(p.amount || 0);
              return (
                <li
                  key={p.id}
                  style={{ "--i": Math.min(i, 12) }}
                  className={s.feedRow}>
                  <button
                    type="button"
                    className={s.feedRowBtn}
                    onClick={() => setSelectedPaymentId(p.id)}
                    aria-label={`Open payment details for ${stName}, ${peso(amt)}`}>
                    <div
                      className={s.feedAvatar}
                      style={{ background: avatarColor(stName) }}
                      aria-hidden="true">
                      {initials(stName)}
                    </div>
                    <div className={s.feedBody}>
                      <div className={s.feedTitle}>
                        <span className={s.feedName}>{stName}</span>
                        <span className={`${s.feedAmount} ${amt < 0 ? s.feedAmountNeg : ""}`}>
                          {amt < 0 ? "−" : ""}{peso(Math.abs(amt))}
                        </span>
                      </div>
                      <div className={s.feedMeta}>
                        {p.orNumber && (
                          <span className={s.orPill}>OR {p.orNumber}</span>
                        )}
                        <span>{p.method || "—"}</span>
                        {p.bankName && <span className={s.feedMetaSep}>·</span>}
                        {p.bankName && <span>{p.bankName}</span>}
                        <span className={s.feedMetaSep}>·</span>
                        <span>{formatDateTime(p.paidAt)}</span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Quick actions — the two daily tools preserved from the prior dashboard,
          now styled as a small action strip at the bottom for fast access. */}
      <section className={s.actionsSection}>
        <header className={s.panelHead}>
          <div>
            <h2 className={s.panelTitle}>Quick Actions</h2>
            <p className={s.panelSub}>The tools you use every day.</p>
          </div>
        </header>
        <div className={s.actionsGrid}>
          <ActionTile
            index={0}
            title="Tuition"
            sub="Fee structures, invoices, and payment recording."
            onClick={() => nav("/admin/tuition")}
            icon={(
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
                   stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="6" width="18" height="12" rx="2" />
                <circle cx="12" cy="12" r="2.5" />
                <path d="M6 10h.01M18 10h.01M6 14h.01M18 14h.01" />
              </svg>
            )} />
          <ActionTile
            index={1}
            title="Statement of Account"
            sub="Per-student ledger: charges, discounts, payments, balance."
            onClick={() => nav("/tuition/soa")}
            icon={(
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
                   stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 3h10a2 2 0 0 1 2 2v14l-3-2-2 2-2-2-2 2-2-2-3 2V5a2 2 0 0 1 2-2Z" />
                <path d="M9 8h6M9 12h6M9 16h4" />
              </svg>
            )} />
        </div>
      </section>

      <RecentAnnouncementsWidget />

      {selectedPaymentId != null && createPortal(
        (() => {
          const p = payments.find(x => x.id === selectedPaymentId);
          if (!p) return null;
          return (
            <PaymentDetailsModal
              payment={p}
              ctx={paymentContext(p)}
              onClose={() => setSelectedPaymentId(null)}
            />
          );
        })(),
        document.body
      )}
    </div>
  );
}

/** Single stat tile. Non-clickable variant — the cashier dashboard lives
 *  on a small number of headline figures, so we don't surface drill-downs
 *  here (the Today / Recent panels below already are the drill-down). */
function StatTile({ index, label, value, sub, tone }) {
  const toneClass =
    tone === "warn"   ? s.statWarn   :
    tone === "ok"     ? s.statOk     :
    tone === "danger" ? s.statDanger : "";
  return (
    <div
      style={{ "--i": index }}
      className={`${s.stat} ${toneClass}`}>
      <div className={s.statLabel}>{label}</div>
      <div className={s.statValue}>{value}</div>
      {sub && <div className={s.statSub}>{sub}</div>}
    </div>
  );
}

/** Quick-action tile at the bottom of the page. Each tile is a button so
 *  the keyboard target is the whole card and screen readers announce a
 *  single navigable control per shortcut. */
function ActionTile({ index, title, sub, onClick, icon }) {
  return (
    <button
      type="button"
      style={{ "--i": index }}
      className={s.actionTile}
      onClick={onClick}>
      <span className={s.actionIcon}>{icon}</span>
      <span className={s.actionTitle}>{title}</span>
      <span className={s.actionSub}>{sub}</span>
      <span className={s.actionArrow}>→</span>
    </button>
  );
}

/** Portaled payment detail modal — same field set as the Reports tab's
 *  PaymentDetailsModal so cashiers see a consistent receipt-style view
 *  across the app. ESC + overlay click + ✕ all dismiss. */
function PaymentDetailsModal({ payment: p, ctx, onClose }) {
  const { invoice, student, section, grade, processor } = ctx;
  const paidAtFull = p.paidAt
    ? new Date(p.paidAt).toLocaleString(undefined,
        { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
  const rows = [
    ["Student",      student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "—"],
    ["Student #",    student?.studentNumber || "—"],
    ["Grade level",  grade?.name   || "—"],
    ["Section",      section?.name || "—"],
    ["Invoice",      invoice ? `#${invoice.id} · ${peso(invoice.amount)}` : "—"],
    ["OR #",         p.orNumber || "—"],
    ["Amount paid",  peso(p.amount)],
    ["Method",       p.method || "—"],
    ["Bank",         p.bankName || "—"],
    ["Reference #",  p.referenceNo || "—"],
    ["Particular",   p.particular || "—"],
    ["Paid at",      paidAtFull],
    ["Processed by", processor?.name || (p.receivedByStaffId ? `Staff #${p.receivedByStaffId}` : "—")],
    ["Notes",        p.notes || "—"],
  ];
  return (
    <div
      className={s.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cash-detail-title">
      <div className={s.modalCard} onClick={e => e.stopPropagation()}>
        <header className={s.modalHead}>
          <h3 id="cash-detail-title" className={s.modalTitle}>Payment details</h3>
          <button
            type="button"
            className={s.modalClose}
            onClick={onClose}
            aria-label="Close payment details">
            ✕
          </button>
        </header>
        <div className={s.modalBody}>
          <dl className={s.detailFacts}>
            {rows.map(([label, value]) => (
              <DetailFact key={label} label={label} value={value} mono={label === "OR #" || label === "Reference #" || label === "Student #"} />
            ))}
          </dl>
        </div>
        <footer className={s.modalFoot}>
          <button type="button" className={s.modalCloseBtn} onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

function DetailFact({ label, value, mono }) {
  return (
    <>
      <dt className={s.detailFactLabel}>{label}</dt>
      <dd className={`${s.detailFactValue} ${mono ? s.detailFactMono : ""}`}>{value}</dd>
    </>
  );
}
